"use client";

// Oversized by design: one WebGL effect whose setup, loop and teardown share
// closure state. Everything with a seam of its own is already a sibling
// module (atlas, chrome, player, shaders, badge, theme).

/**
 * The ad wall behind the sign-in screen.
 *
 * A hidden grid of mobile creatives scrolls slowly in alternating columns. At
 * rest nothing shows: every pixel resolves to the page's own background, so
 * the screen looks exactly as it does without this component. Cards flip open
 * around the cursor and close as it leaves. When the pointer goes still, or
 * while someone is typing, the reveal orbits the login block on its own — a
 * frozen half-flip reads as a stall rather than a rest.
 *
 * THE CARDS ARE REAL ADS. Ten client creatives, as posters first and then as
 * video (`adWallAtlas` lists them, `adWallPlayer` fills the atlas). Each
 * poster goes up the moment it lands; the videos stream in behind, each one
 * taking over its card as it starts to play. A card whose video never
 * arrives keeps its poster, so nothing waits on the network.
 *
 * WHY IT IS A SHADER. Forty cards, each independently scaled per frame, is a
 * compositing problem rather than a layout one — as DOM nodes it would be
 * forty elements with transforms recalculating on every pointer move. One
 * fragment shader does it in a single draw call with no layout at all.
 *
 * THEME COMES FROM TOKENS, NOT CONSTANTS. `u_bg` has to match the page exactly
 * or the canvas reads as a rectangle sitting on the page, so it is read from
 * `--nf-ds-surface-ground` rather than written down here. The glow reads the
 * two stops of `--nf-ds-brand-grad`, which is the same gradient the headline
 * uses. A theme change updates uniforms only; the atlas is theme-independent
 * (see `adWallAtlas`) and is never rebuilt for one.
 *
 * NO ADDITIVE BLENDING. Everything mixes toward `u_bg` or toward the gradient.
 * Additive glow is invisible on cream and blows out to white, which is how a
 * dark-only effect betrays itself the moment someone flips the theme.
 *
 * CONTAINED, NOT FIXED. `/prototypes/one` renders simulated devices, several
 * at once on the rack. A fixed canvas escapes the phone and covers the page —
 * the same trap `Modal` documents with its `contained` prop. This positions
 * against its parent, so it fills whichever frame it is dropped into.
 */

import { useEffect, useRef, useState } from "react";
import { buildBadge } from "./adRevealBadge";
import { compile, FRAG, VERT } from "./adRevealShaders";
import { parseColor, readGradient } from "./adRevealTheme";
import { createWallPlayer } from "./adWallPlayer";

/**
 * Effect strengths, per theme. Not tokens: these are shader mix factors, not
 * a colour, spacing or type decision, and inventing design-system entries for
 * them would put two numbers in the global system that only this file reads.
 * Light gets less of both — the same glow that reads as a faint edge on
 * near-black reads as a smudge on paper.
 */
const GLOW = { light: 0.18, dark: 0.25 };
const GUARD_MIN = { light: 0.25, dark: 0.3 };

/**
 * The ambient state.
 *
 * A still cursor used to leave the cards frozen mid-flip, which reads as a
 * stalled animation rather than a resting one — and while someone is typing
 * their email the pointer is nowhere near the wall. So after a pause the
 * reveal takes over and orbits the login block on its own, and hands control
 * straight back the moment the pointer moves.
 *
 * It orbits rather than wanders: the path is anchored to the block the reader
 * is looking at, so the motion stays in peripheral vision instead of pulling
 * the eye to a corner. Two different frequencies keep it from reading as a
 * clock hand.
 */
const IDLE_AFTER_MS = 1800;
const IDLE_SPEED = 0.22;

/**
 * The same orbit, taken briskly, on a phone layout.
 *
 * The reveal is a fixed ~210px spot, which is a small patch of a desk window
 * and half the width of a phone. At the desk speed that spot crawls across a
 * narrow screen, so every card spends a long time part-open and the whole
 * thing reads as sluggish rather than ambient — the cards look like they are
 * struggling to open rather than passing by.
 *
 * Roughly an 11-second lap against the desk's 28.
 */
const IDLE_SPEED_NARROW = 0.55;
/**
 * The orbit's radius, as a fraction of the guard's half-size.
 *
 * A ratio rather than a margin outside the block. `guard + 120px` sounded
 * modest and was not: on a 448px column it swings the reveal ±384px, so the
 * cards spend most of the loop beside the form rather than around it, and at
 * either extreme they read as an unrelated cluster off in the corner.
 *
 * Under 1, the path stays inside the block's own footprint — the reveal
 * circles the copy it belongs to, and the guard dims whatever crosses behind
 * the text.
 */
const IDLE_ORBIT_RATIO = 0.75;

/**
 * Below this, the reveal is always ambient and never follows a pointer.
 *
 * A narrow layout is a phone in every way that matters here: if a pointer
 * exists at all it is a finger, and a reveal that tracks it sits under the
 * hand holding the device. Matches the `@2xl` container step the rest of One
 * uses to tell a phone from a tablet.
 */
const MOBILE_MAX_CSS_PX = 672;

/** Padding around the login block before the reveal is damped, in CSS px. */
const GUARD_PAD_X = 40;
const GUARD_PAD_Y = 30;

export function AdRevealBackground({
  guardRef,
}: {
  guardRef: React.RefObject<HTMLElement | null>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  /**
   * Bumped when the GPU hands the context back, to re-run the whole effect.
   *
   * A restored context comes back empty: the program, the buffer, the texture
   * and the atlas are all gone with it, so resuming the loop would draw
   * nothing forever. Re-running setup is the only honest recovery, and making
   * it a dependency means the teardown below runs first for free.
   */
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    if (!canvas || !host) return;

    const gl =
      canvas.getContext("webgl", { antialias: false, alpha: false }) ??
      (canvas.getContext("experimental-webgl") as WebGLRenderingContext | null);
    if (!gl) return;

    // A context can also be lost for reasons outside this component — a GPU
    // reset, or too many live contexts on the page. Without this the loop
    // keeps issuing no-op draws forever; the default action also has to be
    // prevented for a restore to ever fire.
    const onLost = (e: Event) => {
      // preventDefault is what makes a restore possible at all.
      e.preventDefault();
      pause();
    };
    const onRestored = () => setGeneration((n) => n + 1);
    canvas.addEventListener("webglcontextlost", onLost);
    canvas.addEventListener("webglcontextrestored", onRestored);

    if (gl.isContextLost())
      return () => {
        canvas.removeEventListener("webglcontextlost", onLost);
        canvas.removeEventListener("webglcontextrestored", onRestored);
      };

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;
    const prog = gl.createProgram();
    if (!prog) return;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      gl.deleteProgram(prog);
      return;
    }
    // biome-ignore lint/correctness/useHookAtTopLevel: WebGL's useProgram matches the use* naming rule; it is not a React hook.
    gl.useProgram(prog);

    // One triangle that covers the clip cube — cheaper than two, and avoids
    // the diagonal seam a quad can show under some drivers.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );
    const loc = gl.getAttribLocation(prog, "a_pos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const u = (n: string) => gl.getUniformLocation(prog, n);
    const uRes = u("u_res");
    const uMouse = u("u_mouse");
    const uGuardC = u("u_guardC");
    const uGuardS = u("u_guardS");
    const uTime = u("u_time");
    const uHover = u("u_hover");
    const uDpr = u("u_dpr");
    const uBg = u("u_bg");
    const uC1 = u("u_c1");
    const uC2 = u("u_c2");
    const uGlow = u("u_glow");
    const uGuardMin = u("u_guardMin");

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // 1×1 transparent until the player allocates the atlas, so the first
    // frames are pure bg.
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      1,
      1,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      new Uint8Array([0, 0, 0, 0]),
    );
    // No mipmaps: they average across tile boundaries and draw seam lines
    // between neighbouring cards.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(u("u_tex"), 0);

    // The badge lives on its own unit. Keeping it off the card atlas means the
    // wall's artwork is never rebuilt to change a badge, and this texture is
    // 512x512 against the atlas's 3072.
    const badgeTex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, badgeTex);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      1,
      1,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      new Uint8Array([0, 0, 0, 0]),
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(u("u_badge"), 1);
    gl.activeTexture(gl.TEXTURE0);

    // The badge and the chrome are drawn after first paint so they never
    // delay the form. The face has to be loaded first or canvas silently
    // measures a fallback and the glyphs land at the wrong size.
    let cancelled = false;
    // The token's value is pretty-printed across two lines by the generator,
    // and a newline makes `800 20px <stack>` an unparseable font shorthand:
    // `ctx.font` silently ignores the assignment and `fonts.load` rejects, so
    // the atlas never uploads and the wall stays invisible. Collapse it.
    const family =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--nf-ds-font-sans")
        .replace(/\s+/g, " ")
        .trim() || "system-ui, sans-serif";
    /** `fonts.load` wants one family, not the whole fallback stack. */
    const firstFamily = family.split(",")[0].trim();

    const ready: Promise<void> = (() => {
      try {
        if (!document.fonts) return Promise.resolve();
        return Promise.race([
          document.fonts.load(`800 20px ${firstFamily}`).catch(() => null),
          new Promise<void>((r) => setTimeout(r, 1500)),
        ]).then(() => undefined);
      } catch {
        // A face that cannot be waited for is still worth drawing with.
        return Promise.resolve();
      }
    })();

    // Fills the atlas: posters as they land, then video where it is wanted.
    const player = createWallPlayer(gl, tex, family, ready);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

    let dpr = 1;
    let disposed = false;
    /**
     * Measured on the host, not the viewport.
     *
     * `/prototypes/one` renders phone-sized frames inside a desktop window, so
     * a viewport media query would call those frames wide. The element the
     * canvas fills is the only honest answer to "how much room is there".
     */
    let narrow = false;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { width, height } = host.getBoundingClientRect();
      narrow = width < MOBILE_MAX_CSS_PX;
      syncPlayer();
      const w = Math.max(1, Math.round(width * dpr));
      const h = Math.max(1, Math.round(height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
      gl.uniform2f(uRes, w, h);
      gl.uniform1f(uDpr, dpr);
    };

    /**
     * Whether video should run is a function of three things that all change
     * on their own: the host's width, the motion preference and the tab's
     * visibility. Re-asked whenever any of them moves, so a preference
     * flipped mid-session pauses the wall's videos and a widened window
     * starts them.
     */
    const syncPlayer = () =>
      player.sync({
        narrow,
        reduced: reduced.matches,
        hidden: document.hidden,
      });

    let gcx = 0;
    let gcy = 0;
    let gsx = 1;
    let gsy = 1;

    const guard = () => {
      const el = guardRef.current;
      if (!el) return;
      const g = el.getBoundingClientRect();
      const b = host.getBoundingClientRect();
      gcx = (g.left - b.left + g.width / 2) * dpr;
      gcy = (g.top - b.top + g.height / 2) * dpr;
      gsx = Math.max(1, (g.width / 2 + GUARD_PAD_X) * dpr);
      gsy = Math.max(1, (g.height / 2 + GUARD_PAD_Y) * dpr);
      gl.uniform2f(uGuardC, gcx, gcy);
      gl.uniform2f(uGuardS, gsx, gsy);
    };

    const applyTheme = () => {
      // Read the HOST, not <html>.
      //
      // The job of `u_bg` is to match the element this canvas sits on, so the
      // honest source is that element's own painted background rather than a
      // token looked up somewhere else. Reading the root assumed `.dark` lives
      // on <html>; the generated CSS uses a bare `.dark` selector, so it can
      // flip a subtree while the root still computes light — and then the
      // canvas paints the light ground over a dark page. Asking the host
      // removes the assumption entirely, and follows `.ds-next` or any other
      // scoped override for free.
      const hostCs = getComputedStyle(host);
      const bg =
        parseColor(hostCs.backgroundColor) ??
        parseColor(hostCs.getPropertyValue("--nf-ds-surface-ground"));
      if (bg) gl.uniform3f(uBg, bg[0], bg[1], bg[2]);

      const [c1, c2] = readGradient(hostCs);
      gl.uniform3f(uC1, c1[0], c1[1], c1[2]);
      gl.uniform3f(uC2, c2[0], c2[1], c2[2]);

      // Which theme, judged from the ground itself rather than a class name.
      // Same reason: the luminance of what we are painting on is the fact the
      // strengths actually depend on.
      const dark = bg
        ? bg[0] * 0.299 + bg[1] * 0.587 + bg[2] * 0.114 < 0.5
        : false;
      gl.uniform1f(uGlow, dark ? GLOW.dark : GLOW.light);
      gl.uniform1f(uGuardMin, dark ? GUARD_MIN.dark : GUARD_MIN.light);
    };

    resize();
    guard();
    applyTheme();
    // One more read after the first paint. next-themes writes the class in a
    // passive effect, so a mount-time read can precede it by a frame.
    requestAnimationFrame(applyTheme);
    reduced.addEventListener("change", syncPlayer);

    // Pointer state. Targets are set by events; the rendered values chase them
    // so a fast flick still draws a continuous path rather than jumping.
    let tx = -1e4;
    let ty = -1e4;
    let mx = -1e4;
    let my = -1e4;
    let hoverTarget = 0;
    let hover = 0;

    let lastMoveAt = 0;
    let typing = false;
    let idleAngle = 0;
    let wasIdle = false;

    const track = (cx: number, cy: number) => {
      const b = host.getBoundingClientRect();
      tx = (cx - b.left) * dpr;
      ty = (cy - b.top) * dpr;
      hoverTarget = 1;
      lastMoveAt = performance.now();
      wasIdle = false;
    };
    const onPointer = (e: PointerEvent) => track(e.clientX, e.clientY);
    const onTouch = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) track(t.clientX, t.clientY);
    };
    const onLeave = () => {
      hoverTarget = 0;
    };

    // Typing counts as idle: the hands are on the keyboard, and freezing the
    // wall mid-flip for the length of an email address is the worst case of
    // the stalled-animation problem.
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      typing = !!t?.closest("input, textarea");
    };
    const onFocusOut = () => {
      typing = false;
    };
    host.addEventListener("focusin", onFocusIn);
    host.addEventListener("focusout", onFocusOut);
    host.addEventListener("pointermove", onPointer, { passive: true });
    host.addEventListener("touchmove", onTouch, { passive: true });
    host.addEventListener("pointerleave", onLeave);
    window.addEventListener("blur", onLeave);

    const ro = new ResizeObserver(() => {
      resize();
      guard();
    });
    ro.observe(host);
    if (guardRef.current) ro.observe(guardRef.current);

    let raf = 0;
    const start = performance.now();
    let lastFrame = start;

    const frame = () => {
      if (disposed) return;
      // Frozen for reduced motion: the columns stop scrolling, but the
      // cursor-driven flip stays, because it answers the user's own input
      // rather than animating on its own.
      const now = performance.now();
      // Real elapsed time, not an assumed 1/60: on a 120Hz display a fixed
      // step runs the orbit at double speed, and after a tab is backgrounded
      // the first frame would otherwise jump.
      const dt = Math.min((now - lastFrame) / 1000, 1 / 20);
      lastFrame = now;
      const t = reduced.matches ? 0 : (now - start) / 1000;

      // `narrow` first and unconditional: on a phone layout the orbit is the
      // only mode, so a stray pointermove cannot hand the reveal to a cursor
      // that is really a thumb. Otherwise ambient whenever nothing else is
      // driving it — a still pointer, or a reader typing.
      const idle = narrow || typing || now - lastMoveAt > IDLE_AFTER_MS;
      if (idle) {
        if (!wasIdle) {
          // Pick up the angle the pointer left off at, so the handover is a
          // continuation rather than a jump to the top of the circle.
          idleAngle = Math.atan2(my - gcy, mx - gcx);
          wasIdle = true;
        }
        // Reduced motion holds the position instead of dropping the reveal.
        // The preference asks for no autonomous *movement*, not for a blank
        // background — so the cards are there, lit and still, and the columns
        // are already frozen because `u_time` is pinned. A wall that simply
        // vanishes for these readers is the design withheld, not respected.
        if (!reduced.matches)
          idleAngle += (narrow ? IDLE_SPEED_NARROW : IDLE_SPEED) * dt;
        tx = gcx + Math.cos(idleAngle) * gsx * IDLE_ORBIT_RATIO;
        ty = gcy + Math.sin(idleAngle * 0.9) * gsy * IDLE_ORBIT_RATIO;
        hoverTarget = 1;
      }

      player.uploadFrames();

      mx += (tx - mx) * 0.16;
      my += (ty - my) * 0.16;
      // 0.06 was about a second to close — long enough that leaving the frame
      // read as the wall ignoring you. 0.12 still eases rather than cuts.
      // Reduced motion snaps: a one-second fade-up is the animation it asked
      // us not to play.
      hover = reduced.matches
        ? hoverTarget
        : hover + (hoverTarget - hover) * 0.12;
      gl.uniform1f(uTime, t);
      gl.uniform1f(uHover, hover);
      gl.uniform2f(uMouse, mx, my);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(frame);
    };

    const play = () => {
      if (raf || disposed) return;
      lastFrame = performance.now();
      raf = requestAnimationFrame(frame);
    };
    const pause = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const onVisibility = () => {
      if (document.hidden) pause();
      else play();
      syncPlayer();
    };
    document.addEventListener("visibilitychange", onVisibility);
    play();

    ready.then(() => {
      if (cancelled || disposed) return;
      // Success green, resolved here so the sprite ships finished rather than
      // being tinted in the shader.
      const green =
        getComputedStyle(document.documentElement)
          .getPropertyValue("--nf-ds-success")
          .trim() || "#22c55e";
      const badge = buildBadge(family, green);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, badgeTex);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        badge,
      );
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.activeTexture(gl.TEXTURE0);
    });

    const onResize = () => {
      resize();
      guard();
    };
    window.addEventListener("resize", onResize);

    // Watch the class on <html>, not next-themes' resolvedTheme.
    //
    // The variables this reads live behind `.dark`, so the DOM is the source
    // of truth and next-themes is only one of the things that writes it.
    // Keying off `resolvedTheme` meant reading the tokens at mount, before
    // the class had necessarily landed, and then never correcting: the value
    // was already "dark" so it never *changed*, and the canvas kept painting
    // the light ground over a dark page for the life of the session.
    //
    // An observer also survives anything else toggling the class, and never
    // tears down the GL context — a theme flip moves uniforms only.
    const themeObserver = new MutationObserver(applyTheme);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    themeObserver.observe(host, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    return () => {
      disposed = true;
      cancelled = true;
      pause();
      player.dispose();
      reduced.removeEventListener("change", syncPlayer);
      themeObserver.disconnect();
      canvas.removeEventListener("webglcontextlost", onLost);
      // Both, not just the loss one. A restore re-runs this effect, so a
      // teardown that leaves the restore listener behind stacks another on
      // every recovery — each holding its own stale closure.
      canvas.removeEventListener("webglcontextrestored", onRestored);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("blur", onLeave);
      host.removeEventListener("focusin", onFocusIn);
      host.removeEventListener("focusout", onFocusOut);
      host.removeEventListener("pointermove", onPointer);
      host.removeEventListener("touchmove", onTouch);
      host.removeEventListener("pointerleave", onLeave);
      ro.disconnect();
      gl.deleteTexture(tex);
      gl.deleteTexture(badgeTex);
      gl.deleteBuffer(buf);
      gl.deleteProgram(prog);
      // Deliberately NOT `WEBGL_lose_context().loseContext()`.
      //
      // StrictMode runs effects twice in development: mount, clean up, mount
      // again. A lost context can never be revived and `getContext` hands back
      // the same dead one, so losing it here leaves the second mount holding a
      // corpse — every draw call is a silent no-op and the canvas renders
      // nothing for the life of the page. Deleting the three objects above
      // releases what we allocated; the context itself goes when the canvas is
      // collected.
    };
  }, [guardRef, generation]);

  return (
    <canvas
      ref={canvasRef}
      // No aria-hidden: biome counts <canvas> as focusable, and an empty
      // canvas with no accessible name already exposes nothing to a reader.
      className="pointer-events-none absolute inset-0 size-full"
    />
  );
}
